import { useRef, useEffect, useState } from 'react';
import { Map, Marker, NavigationControl, Popup } from 'maplibre-gl';
import {
  Box,
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  styled,
  Typography
} from '@mui/material';
import * as AWS from 'aws-sdk/global';
import { Signer } from '@aws-amplify/core';
import 'maplibre-gl/dist/maplibre-gl.css';
import './SecurityDashboardMap.css';
import { Loading } from '@bumble/app/components/Loading';

interface NodeData {
  latitude: number;
  longitude: number;
  node_id: string;
  node_name: string;
  site_address: string;
  malicious_ips: number;
  total_ips: number;
}

type SecurityServicesMapProps = {
  devices: any[];
  nodeData: NodeData;
  type: 'inbound' | 'outbound' | 'blocked';
  loading:  boolean;
};

export const SecurityServicesMap = ({ devices = [], nodeData, type, loading }: SecurityServicesMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const lng = 0;
  const lat = 0;
  const zoom = 0;
  const env = import.meta.env;
  const IdentityPoolId = env.VITE_AWS_IDENTITY_POOL_ID;
  const mapName = env.VITE_MAP_NAME;
  const [percentage, setPercentage] = useState<number>(0);

  let map: any;

  AWS.config.region = IdentityPoolId.split(':')[0];
  const credentials = new AWS.CognitoIdentityCredentials({ IdentityPoolId });

  const refreshCredentials = async () => {
    await credentials.refreshPromise();
    setTimeout(refreshCredentials, credentials.expireTime.valueOf() - new Date().valueOf());
  };

  const transformRequest = (url: string, resourceType: any) => {
    if (resourceType === 'Style' && !url.includes('://')) {
      url = `https://maps.geo.${AWS.config.region}.amazonaws.com/maps/v0/maps/${url}/style-descriptor`;
    }
    if (url.includes('amazonaws.com')) {
      return {
        url: Signer.signUrl(url, {
          access_key: credentials.accessKeyId,
          secret_key: credentials.secretAccessKey,
          session_token: credentials.sessionToken,
        })
      }
    }
    return { url };
  }

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: '#4CAF50',
    },
    [`& .${linearProgressClasses.bar}`]: {
      backgroundColor: '#F44336',
    },
  }));

  function LinearProgressWithLabel(
    props: LinearProgressProps & { value: number; malicious: number; total: number }
  ) {
    return (
      <Box sx={{ width: '100%', maxWidth: 300 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ color: '#F44336', fontWeight: 600 }}>
            {props.malicious}
          </Typography>
          <Typography sx={{ color: '#F44336', fontWeight: 600 }}>
            Total IPs: {props.total ?? 0}
          </Typography>
        </Box>

        {/* Progress bar with overlaid percentage */}
        <Box sx={{ position: 'relative' }}>
          <BorderLinearProgress
            variant="determinate"
            {...props}
            sx={{ height: 30 }}
          />
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              pl: 2,
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            {`${Math.round(props.value)}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  useEffect(() => {
    const percent = nodeData?.total_ips
      ? (nodeData.malicious_ips * 100) / nodeData.total_ips
      : 0;
    setPercentage(percent);
    refreshCredentials().then(() => {
      if (map) return;
      map = new Map({
        container: mapContainer.current!,
        style: mapName,
        center: [lng, lat],
        zoom: zoom,
        hash: false,
        transformRequest,
      });
      map.addControl(new NavigationControl(), 'bottom-right');
      map.on('load', () => {
        if (devices) {
          for (const device of devices) {
            const isBlocked = type === 'blocked';
            const popup = new Popup({ offset: 35 }).setHTML(isBlocked
              ? `<h3>${device.city}, ${device.country}</h3>
                  <p>
                    <strong>IPs:</strong><br/>
                    ${device.ips.map((ip: any) => `${ip.ip} (score: ${ip.malicious_score})`).join('<br/>')}
                  </p>`
              : `<h3>${device.city}, ${device.country}</h3>
                  <p>
                    <strong>IP:</strong>
                    ${device.ips.map((ip: any) => `${ip.ip_address}
                      ${ip.malicious_score === 100 ? '(malicious)':
                        ip.malicious_score === 0 ? '' : `(score: ${ip.malicious_score})`
                      }`).join('<br/>')
                    }
                  </p>
                  <p><strong>Ports:</strong> ${device.ips.map((ip: any) => `${ip.ports}`).join(', ')}</p>`
            );

            const el = document.createElement('div');
            el.style.width = '20px';
            el.style.height = '20px';
            el.style.display = 'flex';
            el.style.alignItems = 'center';
            el.style.justifyContent = 'center';
            el.style.fontSize = '20px';
            el.style.fontWeight = 'bold';
            el.style.color = 'red';
            el.style.cursor = 'pointer';

            if (isBlocked) {
              el.textContent = '×';
              el.style.fontSize = '30px';
            } else {
              el.style.borderRadius = '50%';
              el.style.backgroundColor = 'white';
              if (device.malicious_score === 0) {
                el.style.border = '5px solid blue';
              } else {
                el.style.border = '5px solid red';
              }
              el.style.boxShadow = `0 0 5px rgba(0,0,0,0.5)`;
            }

            const marker = new Marker({ element: el })
              .setLngLat([device.longitude, device.latitude])
              .setPopup(popup)
              .addTo(map);
          }
        }

        if (nodeData) {
          const popup = new Popup({ offset: 35 }).setHTML(`
            <h3>${nodeData.node_name},</h3>
            <h3>${nodeData.node_id}</h3>
            <h3>${nodeData.site_address}</h3>
          `);
          const marker = new Marker({ color: 'green' })
            .setLngLat([nodeData.longitude, nodeData.latitude])
            .setPopup(popup)
            .addTo(map);
        }
      });
    })
  }, [devices, nodeData, type]);

  return (
    <div className="security-map-wrap">
      <h2 className="security-map-title">
        {type === 'inbound'
          ? 'Originating Traffic Geolocations'
          : type === 'outbound'
            ? 'Destination Traffic Geolocations'
            : 'Blocked Malicious IPs'}
        <br/>
        {(nodeData && type !== 'blocked' && !loading) &&
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, mt: 5, mb: 12 }}>
            <Typography sx={{ fontWeight: 600, mt:6 }}>
              Malicious IPs
            </Typography>
            <LinearProgressWithLabel
              value={percentage}
              malicious={nodeData?.malicious_ips}
              total={nodeData?.total_ips}
              color="primary"
              sx={{ height: 30 }}
            />
          </Box>
        }
      </h2>
      {
        loading ?
          <Loading height={435}/> :
          <div ref={mapContainer} className="security-map" />
      }
    </div>
  );
};